import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dueCount } from '../utils/srs.js';
import { getLevel, getNextLevel, getXP, getStreak, getLevelsForRole } from './PerformanceDashboard.jsx';

// ── Navigation button helper ──────────────────────────────────────────────────
const NavigateBtn = ({ to, className, children }) => {
    const navigate = useNavigate();
    return (
        <button type="button" className={className} onClick={() => navigate(to)}>
            {children}
        </button>
    );
};

// ── Small reusable stat chip ──────────────────────────────────────────────────
const StatChip = ({ icon, label, value, highlight = false }) => (
    <div className={`flex items-center gap-2 rounded-xl px-3 py-2 border transition ${
        highlight
            ? 'bg-amber-500/10 border-amber-400/20 shadow-sm shadow-amber-400/10'
            : 'bg-white border-slate-100 shadow-sm dark:bg-slate-900/70 dark:border-white/10 dark:shadow-none'
    }`}>
        <span className={`shrink-0 ${highlight ? 'text-amber-400' : 'text-slate-400'}`}>{icon}</span>
        <div className="leading-tight">
            <span className="text-[11px] text-slate-500 block">{label}</span>
            <span className={`text-sm font-semibold ${highlight ? 'text-amber-300' : 'text-slate-900 dark:text-white'}`}>{value}</span>
        </div>
    </div>
);

// ── Section heading ───────────────────────────────────────────────────────────
const SectionHeading = ({ children }) => (
    <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-3">{children}</h3>
);

// ── Folder study card ─────────────────────────────────────────────────────────
const FolderStudyCard = ({ folder }) => {
    const navigate = useNavigate();
    const cards = Array.isArray(folder.cards) ? folder.cards : null;
    const due = cards ? dueCount(cards) : null;
    const total = folder.cardCount || cards?.length || 0;
    const initial = (folder.name || '?').slice(0, 1).toUpperCase();
    const goToDeck = () => navigate(`/app/deck/${folder.id}`);

    return (
        <div
            className="rounded-2xl border border-slate-100 bg-white shadow-sm hover:border-slate-200 hover:shadow-md dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none dark:hover:border-white/20 transition-all duration-150 cursor-pointer flex flex-col gap-3 p-4"
            onClick={goToDeck}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToDeck(); } }}
        >
            <div className="flex items-center gap-2.5 min-w-0">
                <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${folder.color || 'from-teal-500 to-emerald-600'} flex items-center justify-center shrink-0`}>
                    <span className="text-white text-xs font-bold">{initial}</span>
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{folder.name}</p>
                    <p className="text-[11px] mt-0.5">
                        {due !== null ? (
                            due > 0
                                ? <span className="text-amber-500 dark:text-amber-400 font-medium">{due} due today</span>
                                : <span className="text-emerald-500 dark:text-emerald-400">All caught up ✓</span>
                        ) : (
                            <span className="text-slate-400">{total} card{total !== 1 ? 's' : ''}</span>
                        )}
                    </p>
                </div>
            </div>
            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                <button
                    onClick={goToDeck}
                    className="flex-1 rounded-xl py-2 text-xs font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 transition shadow-sm shadow-teal-500/20"
                >
                    Open Deck →
                </button>
            </div>
        </div>
    );
};

// ── Main dashboard ────────────────────────────────────────────────────────────
const HomeDashboard = ({
    user,
    folders = [],
    mostRecentScore,
    entitlements,
    onStudyFolder,
    onPlayGame,
    onLaunchNemesis,
    onStartExam,
}) => {
    const greeting = useMemo(() => {
        const h = new Date().getHours();
        const g = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
        const name = user && !user.isAnonymous
            ? (user.displayName || (user.email || '').split('@')[0])
            : '';
        return name ? `${g}, ${name}` : g;
    }, [user]);

    const streakData = useMemo(() => getStreak(), []);
    const streak = streakData.count;

    const xp = useMemo(() => getXP(), []);
    const levels = useMemo(() => {
        try {
            const profile = JSON.parse(localStorage.getItem('ff:onboarding:profile') || 'null');
            return getLevelsForRole(profile?.role);
        } catch { return getLevelsForRole(null); }
    }, []);
    const level = useMemo(() => getLevel(xp, levels), [xp, levels]);
    const nextLevel = useMemo(() => getNextLevel(xp, levels), [xp, levels]);
    const maxLevel = levels[levels.length - 1];
    const levelPct = useMemo(() => {
        if (!nextLevel) return 100;
        return Math.round(((xp - level.minXP) / (nextLevel.minXP - level.minXP)) * 100);
    }, [xp, level, nextLevel]);

    const totalDue = useMemo(() =>
        folders.reduce((sum, f) => Array.isArray(f.cards) ? sum + dueCount(f.cards) : sum, 0),
    [folders]);

    const creditsDisplay = useMemo(() => {
        const c = entitlements?.creditsBalance;
        return typeof c === 'number' ? c.toLocaleString() : '—';
    }, [entitlements]);

    // Top 3 folders for "Continue Studying" — most due first, fall back to most recently updated
    const studyFolders = useMemo(() => {
        return [...folders]
            .filter(f => (f.cardCount || 0) > 0 || (f.cards?.length ?? 0) > 0)
            .sort((a, b) => {
                const aDue = Array.isArray(a.cards) ? dueCount(a.cards) : -1;
                const bDue = Array.isArray(b.cards) ? dueCount(b.cards) : -1;
                if (bDue !== aDue) return bDue - aDue;
                return (b.updatedAt || 0) - (a.updatedAt || 0);
            })
            .slice(0, 3);
    }, [folders]);

    // Most recent folder for game quick-launch
    const recentFolder = useMemo(() =>
        [...folders]
            .filter(f => (f.cardCount || 0) > 0)
            .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))[0] || null,
    [folders]);

    const hasFolders = folders.some(f => (f.cardCount || 0) > 0);

    // Cross-deck mastery (only from folders with cards loaded)
    const globalMastery = useMemo(() => {
        let total = 0, mastered = 0;
        folders.forEach(f => {
            if (Array.isArray(f.cards) && f.cards.length > 0) {
                total += f.cards.length;
                mastered += f.cards.filter(c => (c.srsRepetitions ?? 0) >= 3).length;
            }
        });
        return total > 0 ? { total, mastered, pct: Math.round((mastered / total) * 100) } : null;
    }, [folders]);

    // 7-day activity dots using streak data
    const activityDots = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const dots = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
            const daysAgo = i;
            const active = streakData.lastDate >= d && (streak > daysAgo || streakData.lastDate === d);
            dots.push({ date: d, active, isToday: d === today });
        }
        return dots;
    }, [streakData, streak]);

    return (
        <div className="space-y-8">

            {/* ── Greeting + stats ── */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">{greeting}!</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Your clinical study dashboard</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <StatChip
                        icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"/></svg>}
                        label="Study Streak"
                        value={`${streak} day${streak !== 1 ? 's' : ''}`}
                    />
                    <StatChip
                        icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg>}
                        label="Study Points"
                        value={xp.toLocaleString()}
                    />
                    {totalDue > 0 && (
                        <StatChip
                            icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                            label="Review Due"
                            value={totalDue}
                            highlight
                        />
                    )}
                    <StatChip
                        icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>}
                        label="Credits"
                        value={creditsDisplay}
                    />
                </div>
            </div>

            {/* ── Continue Studying ── */}
            {studyFolders.length > 0 && (
                <section>
                    <SectionHeading>Continue Studying</SectionHeading>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {studyFolders.map(folder => (
                            <FolderStudyCard
                                key={folder.id}
                                folder={folder}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* ── Your Progress ── */}
            <section>
                <SectionHeading>Your Progress</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Level + Study Points */}
                    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none p-5">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-teal-500 dark:text-teal-400">Level {level.level}</span>
                                <p className="text-lg font-bold text-slate-900 dark:text-white mt-0.5">{level.label}</p>
                            </div>
                            <div className="rounded-xl bg-teal-500/12 border border-teal-500/20 px-3 py-1.5 text-right">
                                <div className="text-lg font-bold tabular-nums text-teal-600 dark:text-teal-300">{xp.toLocaleString()}</div>
                                <div className="text-[11px] text-slate-500">study points</div>
                            </div>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 dark:bg-white/8 overflow-hidden mb-1.5">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-700"
                                style={{ width: `${levelPct}%` }}
                            />
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-400">
                            <span>{level.label}</span>
                            {nextLevel ? (
                                <span>{nextLevel.minXP - xp} pts to {nextLevel.label}</span>
                            ) : (
                                <span className="text-teal-500 dark:text-teal-400">{maxLevel.label}!</span>
                            )}
                        </div>
                    </div>

                    {/* Streak + Activity */}
                    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none p-5">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-amber-500 dark:text-amber-400">Streak</p>
                                <div className="flex items-baseline gap-1 mt-0.5">
                                    <span className="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">{streak}</span>
                                    <span className="text-sm text-slate-500">days</span>
                                </div>
                            </div>
                            <span className="text-3xl select-none">{streak >= 7 ? '🔥' : streak >= 3 ? '⚡' : '✨'}</span>
                        </div>
                        {/* 7-day dots */}
                        <div className="flex items-center gap-1.5">
                            {activityDots.map((d, i) => (
                                <div key={i} className="flex flex-col items-center gap-1 flex-1">
                                    <div className={`h-7 w-full rounded-md transition-all ${
                                        d.active
                                            ? 'bg-gradient-to-b from-amber-400 to-orange-500 shadow-sm shadow-amber-400/30'
                                            : 'bg-slate-100 dark:bg-white/6'
                                    }`} />
                                    <span className={`text-[9px] ${d.isToday ? 'text-amber-500 dark:text-amber-400 font-bold' : 'text-slate-400 dark:text-slate-700'}`}>
                                        {['S','M','T','W','T','F','S'][new Date(d.date + 'T12:00:00').getDay()]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Cross-deck mastery */}
                {globalMastery && (
                    <div className="mt-3 rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-white/8 dark:bg-slate-900/60 dark:shadow-none px-5 py-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-900 dark:text-white">Overall Mastery</span>
                            <span className="text-sm font-bold text-emerald-500 dark:text-emerald-400 tabular-nums">{globalMastery.pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 dark:bg-white/8 overflow-hidden mb-1.5">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-700"
                                style={{ width: `${globalMastery.pct}%` }}
                            />
                        </div>
                        <p className="text-[11px] text-slate-500">
                            {globalMastery.mastered.toLocaleString()} of {globalMastery.total.toLocaleString()} cards mastered across tracked decks
                        </p>
                    </div>
                )}
            </section>


            {/* ── Practice modes ── */}
            <section>
                <SectionHeading>Practice Modes</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Verbatim Master */}
                    <div className="rounded-2xl border border-teal-200 bg-teal-50 dark:border-teal-500/20 dark:bg-teal-500/5 p-4 sm:p-5 flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-xl bg-teal-100 border border-teal-200 dark:bg-teal-600/20 dark:border-teal-500/20 flex items-center justify-center shrink-0">
                                <svg viewBox="0 0 24 24" width="18" height="18" className="text-teal-500 dark:text-teal-300" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Verbatim Master</h3>
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-600 dark:bg-teal-500/20 dark:text-teal-300 ring-1 ring-teal-200 dark:ring-teal-400/20 uppercase tracking-wide">Voice</span>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">Speak your answer — scored in real time</p>
                            </div>
                        </div>
                        {hasFolders ? (
                            <NavigateBtn to={`/app/deck/${recentFolder?.id}`} className="mt-auto w-full rounded-xl py-2 text-xs font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 transition">
                                Play with {recentFolder?.name ? `"${recentFolder.name}"` : 'recent deck'}
                            </NavigateBtn>
                        ) : (
                            <p className="mt-auto text-xs text-slate-400 dark:text-slate-500 text-center py-1">Create a deck to play</p>
                        )}
                    </div>

                    {/* Anamnesis Nemesis */}
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/5 p-4 sm:p-5 flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-xl bg-amber-100 border border-amber-200 dark:bg-amber-600/20 dark:border-amber-500/20 flex items-center justify-center shrink-0">
                                <svg viewBox="0 0 24 24" width="18" height="18" className="text-amber-500 dark:text-amber-300" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 8v4m0 4h.01" />
                                </svg>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Anamnesis Nemesis</h3>
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-400/20 uppercase tracking-wide">Sim</span>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">Beat the Nemesis through your deck — under pressure</p>
                            </div>
                        </div>
                        {hasFolders ? (
                            <NavigateBtn to={`/app/deck/${recentFolder?.id}`} className="mt-auto w-full rounded-xl py-2 text-xs font-semibold text-white bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 transition">
                                Challenge with {recentFolder?.name ? `"${recentFolder.name}"` : 'recent deck'}
                            </NavigateBtn>
                        ) : (
                            <p className="mt-auto text-xs text-slate-400 dark:text-slate-500 text-center py-1">Create a deck to play</p>
                        )}
                    </div>

                    {/* Exam Mode */}
                    <div className="rounded-2xl border border-sky-200 bg-sky-50 dark:border-sky-500/20 dark:bg-sky-500/5 p-4 sm:p-5 flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-xl bg-sky-100 border border-sky-200 dark:bg-sky-600/20 dark:border-sky-500/20 flex items-center justify-center shrink-0">
                                <svg viewBox="0 0 24 24" width="18" height="18" className="text-sky-500 dark:text-sky-300" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    <path d="M9 12l2 2 4-4" />
                                </svg>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Exam Mode</h3>
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-400/20 uppercase tracking-wide">MCQ</span>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">Board-style multiple choice from your own decks</p>
                            </div>
                        </div>
                        <button
                            onClick={onStartExam}
                            className="mt-auto w-full rounded-xl py-2 text-xs font-semibold text-white bg-gradient-to-r from-sky-700 to-sky-500 hover:from-sky-600 hover:to-sky-400 transition"
                        >
                            Start Exam
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Last practice recap ── */}
            {mostRecentScore && (
                <section>
                    <SectionHeading>Last Practice</SectionHeading>
                    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-4">
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
                                <svg viewBox="0 0 24 24" width="18" height="18" className="text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                    {mostRecentScore.name || 'Player'} · {mostRecentScore.level || 'Verbatim Master'}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    Score: <span className="text-amber-500 dark:text-amber-300 font-semibold">{mostRecentScore.score} pts</span>
                                    {mostRecentScore.date && (
                                        <span className="ml-2 text-slate-400">
                                            {new Date(mostRecentScore.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        {recentFolder && (
                            <NavigateBtn to={`/app/deck/${recentFolder.id}`} className="shrink-0 rounded-xl px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition">
                                Play Again
                            </NavigateBtn>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

export default HomeDashboard;
